/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * fcmService.ts
 * Serviço autoritativo CLIENT-SIDE para gerenciamento de notificações FCM e dispositivos (N4-A).
 * 
 * Regras e Garantias (N4-A):
 * 1. Ancorado estritamente no UID autenticado (`userId`). Jamais usa nome, CPF, e-mail ou telefone como ID.
 * 2. Suporta múltiplos dispositivos por usuário gravando em `/users/{userId}/devices/{deviceId}`.
 * 3. O deviceId é estável por navegador/dispositivo (salvo no localStorage).
 * 4. Tratamento resiliente e não-bloqueante para logout, permissão negada ou ausência de VAPID Key.
 * 5. Não cria notificações internas no Firestore e não duplica lógica do N1/N2/N3.
 */

import { db, auth, getFirebaseMessaging } from "../../utils/firebase";
import { doc, setDoc, deleteDoc, getDoc } from "firebase/firestore";
import { getToken, deleteToken, onMessage } from "firebase/messaging";

const DEVICE_ID_KEY = "fcm_device_id_v1";
const LAST_SAVED_TOKEN_KEY = "fcm_last_saved_token_v1";
const LAST_SAVED_UID_KEY = "fcm_last_saved_uid_v1";

let inFlightRegistrationPromise: Promise<RegisterDeviceResult> | null = null;

export interface UserDeviceDocument {
  deviceId: string;
  fcmToken: string;
  deviceType: "pwa" | "android" | "desktop" | "mobile_web";
  userAgent: string;
  createdAt: string;
  updatedAt: string;
}

export interface RegisterDeviceResult {
  success: boolean;
  fcmToken?: string;
  deviceId?: string;
  reason?: "UNSUPPORTED" | "PERMISSION_DENIED" | "PERMISSION_DEFAULT" | "VAPID_KEY_MISSING" | "NO_USER" | "ERROR";
  message?: string;
}

/**
 * Retorna ou gera um ID estável de dispositivo para este navegador
 */
export function getDeviceId(): string {
  if (typeof window === "undefined" || !window.localStorage) {
    return "dev_server_side";
  }
  try {
    let existingId = localStorage.getItem(DEVICE_ID_KEY);
    if (!existingId || !existingId.startsWith("dev_")) {
      existingId = `dev_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem(DEVICE_ID_KEY, existingId);
    }
    return existingId;
  } catch {
    return `dev_fallback_${Date.now()}`;
  }
}

/**
 * Determina o tipo do dispositivo atual
 */
export function detectDeviceType(): "pwa" | "android" | "desktop" | "mobile_web" {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return "desktop";
  }
  const ua = navigator.userAgent.toLowerCase();
  const isStandalone = (typeof window.matchMedia === "function" && window.matchMedia('(display-mode: standalone)').matches) ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;

  if (isStandalone) {
    return "pwa";
  }
  if (/android/.test(ua)) {
    return "android";
  }
  if (/iphone|ipad|ipod|mobile/.test(ua)) {
    return "mobile_web";
  }
  return "desktop";
}

/**
 * Solicita explicitamente permissão de notificação do navegador
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "denied";
  }
  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (err) {
    console.warn("[FcmService] Erro ao solicitar permissão de notificação:", err);
    return "denied";
  }
}

/**
 * Mask FCM token for safe logging
 */
export function maskFcmToken(token?: string): string {
  if (!token || typeof token !== "string") return "empty";
  if (token.length <= 16) return "***";
  return `${token.slice(0, 8)}...${token.slice(-6)}`;
}

/**
 * Registra o dispositivo do usuário logado obtendo o token FCM e persistindo em `/users/{currentUser.uid}/devices/{deviceId}`
 * GARANTIA N4-A: O UID é obtido EXCLUSIVAMENTE de auth.currentUser.uid. Não aceita userId externo.
 */
export async function registerDeviceFCM(params?: {
  forceRequestPermission?: boolean;
}): Promise<RegisterDeviceResult> {
  if (inFlightRegistrationPromise && !params?.forceRequestPermission) {
    return inFlightRegistrationPromise;
  }

  const promise = (async (): Promise<RegisterDeviceResult> => {
    const currentUser = auth?.currentUser;
    const currentUid = currentUser?.uid;

    if (!currentUid || typeof currentUid !== "string" || currentUid.trim().length === 0) {
      return {
        success: false,
        reason: "NO_USER",
        message: "Usuário não autenticado para registrar dispositivo FCM.",
      };
    }

    // 1. Verifica suporte a Web Notifications
    const hasNotificationSupport = typeof Notification !== "undefined" && Notification !== null;
    if (!hasNotificationSupport) {
      return {
        success: false,
        reason: "UNSUPPORTED",
        message: "Notificações Web não suportadas neste navegador.",
      };
    }

    // 2. Verifica permissão de notificação
    let currentPermission = Notification.permission;
    if (currentPermission === "default" && params?.forceRequestPermission) {
      currentPermission = await requestNotificationPermission();
    }

    if (currentPermission === "denied") {
      return {
        success: false,
        reason: "PERMISSION_DENIED",
        message: "Permissão de notificação negada pelo usuário.",
      };
    }

    if (currentPermission === "default") {
      return {
        success: false,
        reason: "PERMISSION_DEFAULT",
        message: "Permissão de notificação pendente (aguardando ação do usuário).",
      };
    }

    // 3. Obtém VAPID Key da variável de ambiente VITE_FIREBASE_VAPID_KEY
    const env = (import.meta as any).env || {};
    const vapidKey = env.VITE_FIREBASE_VAPID_KEY || (typeof process !== "undefined" && process.env ? process.env.VITE_FIREBASE_VAPID_KEY : undefined);

    if (!vapidKey || typeof vapidKey !== "string" || vapidKey.trim().length === 0) {
      console.warn("[FcmService] VAPID Key não configurada (VITE_FIREBASE_VAPID_KEY). Obtenção de token FCM pausada.");
      return {
        success: false,
        reason: "VAPID_KEY_MISSING",
        message: "VAPID Key do Firebase não configurada no ambiente (VITE_FIREBASE_VAPID_KEY).",
      };
    }

    // 4. Obtém instância do Firebase Messaging
    const messaging = await getFirebaseMessaging();
    if (!messaging) {
      return {
        success: false,
        reason: "UNSUPPORTED",
        message: "Firebase Messaging não pôde ser inicializado neste ambiente.",
      };
    }

    try {
      // Obtém ou reaproveita o Service Worker unificado registrado no boot
      let swRegistration: ServiceWorkerRegistration | undefined = undefined;
      if ("serviceWorker" in navigator) {
        try {
          swRegistration = await navigator.serviceWorker.ready;
        } catch (swErr) {
          console.warn("[FcmService] Falha ao obter navigator.serviceWorker.ready:", swErr);
        }

        if (!swRegistration) {
          try {
            swRegistration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
          } catch (swErr) {
            console.warn("[FcmService] Falha ao registrar Service Worker FCM específico:", swErr);
          }
        }
      }

      const fcmToken = await getToken(messaging, {
        vapidKey: vapidKey.trim(),
        serviceWorkerRegistration: swRegistration,
      });

      if (!fcmToken) {
        return {
          success: false,
          reason: "ERROR",
          message: "Não foi possível gerar o token FCM.",
        };
      }

      const deviceId = getDeviceId();

      // Checa se o token e o usuário já foram salvos anteriormente neste dispositivo
      let isCachedLocally = false;
      try {
        const cachedToken = localStorage.getItem(LAST_SAVED_TOKEN_KEY);
        const cachedUid = localStorage.getItem(LAST_SAVED_UID_KEY);
        if (cachedToken === fcmToken && cachedUid === currentUid && !params?.forceRequestPermission) {
          isCachedLocally = true;
        }
      } catch (cacheErr) {}

      // Se o cache local coincide, verifica se o documento ainda existe no Firestore.
      // Se tiver sido excluído, o cache local NÃO impede a recriação do registro necessário.
      if (isCachedLocally && db) {
        try {
          const deviceRef = doc(db, "users", currentUid, "devices", deviceId);
          const snap = await getDoc(deviceRef);
          if (snap.exists()) {
            return {
              success: true,
              fcmToken,
              deviceId,
            };
          }
        } catch {
          // Em caso de erro temporário de rede ou modo offline, preserva retorno pelo cache
          return {
            success: true,
            fcmToken,
            deviceId,
          };
        }
      }

      const deviceDoc: UserDeviceDocument = {
        deviceId,
        fcmToken,
        deviceType: detectDeviceType(),
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Persiste exclusivamente na subcoleção do próprio usuário `/users/{userId}/devices/{deviceId}`
      if (db) {
        const deviceRef = doc(db, "users", currentUid, "devices", deviceId);
        await setDoc(deviceRef, deviceDoc, { merge: true });
      }

      // Atualiza cache local
      try {
        localStorage.setItem(LAST_SAVED_TOKEN_KEY, fcmToken);
        localStorage.setItem(LAST_SAVED_UID_KEY, currentUid);
      } catch (cacheErr) {}

      console.log(`[FcmService] Token FCM registrado com sucesso para dispositivo ${deviceId} (token: ${maskFcmToken(fcmToken)})`);

      return {
        success: true,
        fcmToken,
        deviceId,
      };
    } catch (err: any) {
      console.error("[FcmService] Erro ao registrar dispositivo FCM:", err);
      return {
        success: false,
        reason: "ERROR",
        message: `Erro ao obter token FCM: ${err?.message || String(err)}`,
      };
    }
  })();

  inFlightRegistrationPromise = promise;
  try {
    const res = await promise;
    return res;
  } finally {
    inFlightRegistrationPromise = null;
  }
}

/**
 * Remove o registro do dispositivo atual e invalida o token FCM ao deslogar.
 * GARANTIA N4-A: Prioriza auth.currentUser.uid, remove somente o dispositivo atual e não bloqueia o fluxo de logout.
 */
export async function unregisterCurrentDevice(userIdParam?: string): Promise<{ success: boolean }> {
  // Limpa cache de token salvo localmente
  try {
    localStorage.removeItem(LAST_SAVED_TOKEN_KEY);
    localStorage.removeItem(LAST_SAVED_UID_KEY);
  } catch (e) {}

  const currentUid = auth?.currentUser?.uid || userIdParam;
  if (!currentUid || typeof currentUid !== "string" || currentUid.trim().length === 0) {
    return { success: true };
  }

  const deviceId = getDeviceId();

  // 1. Remove documento na subcoleção do usuário no Firestore
  if (db) {
    try {
      const deviceRef = doc(db, "users", currentUid, "devices", deviceId);
      await deleteDoc(deviceRef);
    } catch (err) {
      console.warn("[FcmService] Falha ao deletar dispositivo do Firestore durante logout:", err);
    }
  }

  // 2. Invalida o token FCM no SDK client
  try {
    const messaging = await getFirebaseMessaging();
    if (messaging) {
      await deleteToken(messaging);
    }
  } catch (err) {
    console.warn("[FcmService] Falha ao revogar token FCM durante logout:", err);
  }

  return { success: true };
}

/**
 * Assina escuta de mensagens FCM recebidas com a aplicação em primeiro plano (foreground)
 * GARANTIA N4-A: NÃO cria notificações no Firestore nem chama createUserNotification().
 */
export async function onForegroundMessage(callback: (payload: any) => void): Promise<(() => void) | null> {
  try {
    const messaging = await getFirebaseMessaging();
    if (!messaging) return null;

    const unsubscribe = onMessage(messaging, (payload) => {
      console.log("[FcmService] Mensagem FCM em primeiro plano recebida:", payload);
      callback(payload);
    });

    return unsubscribe;
  } catch (err) {
    console.warn("[FcmService] Erro ao registrar listener foreground FCM:", err);
    return null;
  }
}
